import React from 'react';
import PropTypes from 'prop-types';

import { DefaultSpinner, NoData } from 'components';
import { useScopedLocationIds } from 'hooks/useAdminGroup/useScopedLocationIds';
import { useHierarchyExternalIds } from 'hooks/useAdminGroup/useHierarchyExternalIds';
import LocationScopeGate from 'hooks/useAdminGroup/LocationScopeGate';
import { deriveScopedApiFilters } from 'hooks/useAdminGroup/deriveScopedApiFilters';

// The Analytics tab does not render through ReportPageChart: its four
// sections (trend, comparison, period, call-duration) pass the Redux
// controls `state.apiFilters` straight into AnalyticsChart, MultiPieContainer
// and the panel/table fetches. This wrapper is the tab's single scope
// boundary -- the admin-group/location breadcrumb scope is applied to
// `state.apiFilters` here, before any section renders, so every widget below
// fetches scoped. Held (gate) while an admin-group scope's subtree is still
// resolving, so sections never briefly fetch account-wide data.
//
// The scope is sent under `filters.scope_locations` -- NOT merged into
// `filters.locations` the way ReportPageChart does for Reporting/Quality.
// The analytics endpoints infer the report bucket from which entity filter
// keys are present (`locations` wins over users/call_groups/teams), the
// custom-group (teams) member expansion ORs entity filters together, and the
// comparison panels rebuild `filters` from scratch per comparator. Merging
// the scope into `locations` would flip comparison/panel bucketing on every
// non-Location view and OR-widen custom-group reports; `scope_locations` is
// consumed by NextosReportBase._resolve_locations and ANDed unconditionally.
// The Location view's own `locations` bucket filter still intersects with
// the scope client-side, and its degraded seed (/nextos/locations returned
// no options) is recovered from the breadcrumb hierarchy -- the full
// decision table lives in scopedApiFilters.js.
const ScopedSection = ({ component: Component, state, actions }) => {
  const scope = useScopedLocationIds();
  const hierarchy = useHierarchyExternalIds();

  const { apiFilters, hold, isEmptyIntersection } = deriveScopedApiFilters({
    apiFilters: state.apiFilters,
    filterOptions: state.filterOptions,
    scope,
    hierarchy,
  });

  return (
    <LocationScopeGate scope={scope}>
      {hold ? (
        <DefaultSpinner />
      ) : isEmptyIntersection ? (
        <NoData />
      ) : (
        <Component state={{ ...state, apiFilters }} actions={actions} />
      )}
    </LocationScopeGate>
  );
};

ScopedSection.propTypes = {
  component: PropTypes.elementType.isRequired,
  state: PropTypes.object.isRequired,
  actions: PropTypes.object,
};

export default ScopedSection;
